export interface TimelineItem {
  date: string
  title: string
  description: string
  side: 'left' | 'right'
  highlight?: boolean
  future?: boolean
}

export interface TimelineDivider {
  type: 'divider'
  label: string
}

export type TimelineEntry = TimelineItem | TimelineDivider

export const timelineData: TimelineEntry[] = [
  {
    date: 'Nov 2023',
    title: 'The Idea',
    description:
      'Max Anderson, frustrated by endless paperwork as a financial adviser, sketches the first concept for WealthSpace.',
    side: 'left',
  },
  {
    date: 'Jan 2024',
    title: 'WealthSpace Incorporated',
    description: 'Company officially registered. The journey begins.',
    side: 'right',
  },
  {
    date: 'May 2024',
    title: 'Development Starts',
    description:
      'Full development kicks off with the team working intensively on the core platform.',
    side: 'left',
  },
  {
    date: 'Jul 2024',
    title: 'Wider Founding Team',
    description: 'The team expands. More expertise, more ambition, more momentum.',
    side: 'right',
  },
  {
    date: 'Feb 2025',
    title: 'Privacy Layer Live',
    description: 'Proprietary privacy layer goes live. Client data stays secure, always.',
    side: 'left',
  },
  {
    date: 'Apr 2025',
    title: 'Bath Offices Open',
    description: 'New headquarters opens. Bespoke prompt architecture goes live.',
    side: 'right',
  },
  {
    date: 'May 2025',
    title: 'Growing the Platform',
    description: 'Continued investment in the platform, with new capabilities shaped by adviser feedback.',
    side: 'left',
  },
  {
    date: 'Aug 2025',
    title: 'Report Builder Live',
    description: 'Full report generation and knowledge base architecture launched.',
    side: 'right',
  },
  { type: 'divider', label: 'The Quantum Leap' },
  {
    date: 'Oct 2025',
    title: 'A Sea Change',
    description:
      'The system and business go through an enormous transformation. New mindset, new architecture, new possibilities.',
    side: 'left',
  },
  {
    date: 'Nov 2025',
    title: 'Community Grows',
    description: 'More UK advice firms join the platform as word spreads across the community.',
    side: 'right',
  },
  {
    date: 'Jan 2026',
    title: 'WealthSpace V2',
    description:
      'The next generation platform goes live. 40+ features. Faster, smarter, more powerful.',
    side: 'left',
  },
  {
    date: 'Feb 2026',
    title: 'WealthSpace V2.1',
    description:
      'Rapid iteration. New integrations, refined workflows, and features shaped by adviser feedback.',
    side: 'right',
  },
  {
    date: 'Mar 2026',
    title: 'Engine Room Launch',
    description: 'The internal operations platform goes live. Powering everything behind the scenes.',
    side: 'left',
    highlight: true,
  },
  {
    date: 'Apr 2026',
    title: 'Keynote Event',
    description: 'Join us for the WealthSpace keynote. The future of advice starts here.',
    side: 'right',
    highlight: true,
  },
  {
    date: '2026',
    title: 'Pathway & Polaris',
    description: 'Coming soon: serve more clients without more hours. Watch this space.',
    side: 'left',
    future: true,
  },
]

export interface Value {
  title: string
  description: string
}

export const values: Value[] = [
  {
    title: 'Client outcomes first',
    description:
      'Everything we build is designed to help advisers deliver better outcomes for their clients.',
  },
  {
    title: 'Compliance without compromise',
    description:
      "We never cut corners on compliance. Our platform is built to support firms' regulatory obligations.",
  },
  {
    title: 'Continuous improvement',
    description:
      'We ship improvements weekly based on user feedback. Your input shapes our roadmap.',
  },
  {
    title: 'Transparency',
    description: 'No hidden fees, no lock-ins. We succeed when you succeed.',
  },
]

export interface TeamMember {
  name: string
  role: string
  bio: string
  initials: string
}

export const team: TeamMember[] = [
  {
    name: 'Max Anderson',
    role: 'CEO & Founder',
    bio: 'Qualified Financial Adviser (DipPFS) who built WealthSpace to solve the problems he experienced daily. Previously at Highclere Wealth.',
    initials: 'MA',
  },
  {
    name: 'Luke Clarke',
    role: 'Chairman',
    bio: 'Providing strategic guidance, governance leadership, and decades of business experience.',
    initials: 'LC',
  },
  {
    name: 'Kevin Brown',
    role: 'Co-Founder & CTO',
    bio: 'Experienced IT professional leading the technical vision, architecture, and engineering.',
    initials: 'KB',
  },
  {
    name: 'Tom Smith',
    role: 'Co-Founder & CFO',
    bio: 'Chartered Accountant overseeing finance, operations, and commercial strategy.',
    initials: 'TS',
  },
  {
    name: 'Dani Nelson',
    role: 'Founding Team',
    bio: 'Driving operational excellence, customer success, and adviser onboarding.',
    initials: 'DN',
  },
  {
    name: 'Simon Smith',
    role: 'Founding Team',
    bio: 'Leading sales and partnerships, bringing WealthSpace to advisers across the UK.',
    initials: 'SS',
  },
  {
    name: 'Gabrielle Anderson',
    role: 'Founding Team',
    bio: 'Focused on growth, marketing, and building adviser relationships.',
    initials: 'GA',
  },
  {
    name: 'Ronnie Hyslop',
    role: 'Founding Team',
    bio: 'Infrastructure Solutions Architect with expertise in complex systems and bespoke application development.',
    initials: 'RH',
  },
]
